import React from "react";
import { MonumentTenant, formatMonumentDate } from "./monumentPayloads";

interface MonumentTenantPickerProps {
    /** Existing tenancies that matched (from getMonumentTargets). */
    tenants: MonumentTenant[];
    /** Active prospects that matched. */
    leads: MonumentTenant[];
    /** Currently selected match's target uuid ("" = none picked). */
    selectedAccountUuid: string;
    /** Called with the chosen match's target uuid when a card is clicked. */
    onSelect: (accountUuid: string) => void;
    /** Unit label the agent chose for a multi-unit tenancy ("" = none narrowed). */
    selectedUnitLabel?: string;
    onSelectUnit?: (unitLabel: string) => void;
    disabled?: boolean;
}

/**
 * Card grid for picking WHICH Monument record a call-summary note attaches to.
 *
 * Shared by the voice and video call-summary modals so they stay in lock-step. Monument
 * returns two kinds of match and a note goes to a different place for each: an existing
 * tenancy is noted on its account, a prospect on its lead. They are shown as two labelled
 * sections rather than one list so the agent can see which is which — picking a Lead card
 * is what stops the call summary from creating a second lead for someone Monument already
 * has. Tenancies are listed first, matching the backend's ordering and the default selection.
 *
 * A single match is rendered as one full-width card rather than a grid tile: it is still the
 * record the note attaches to and deserves the same visual anchor, but there is nothing to
 * choose, so the heading, helper text and click handling are dropped. Without it the modal
 * degenerated into two stacked full-width bars that each repeated the caller's name.
 */
const MonumentTenantPicker: React.FC<MonumentTenantPickerProps> = ({
    tenants,
    leads,
    selectedAccountUuid,
    onSelect,
    selectedUnitLabel = "",
    onSelectUnit,
    disabled = false,
}) => {
    const safeTenants = tenants || [];
    const safeLeads = leads || [];
    const total = safeTenants.length + safeLeads.length;

    // Scroll back to the top when the matched records change. The video modal re-runs its name
    // search while this component stays mounted, and a DOM node keeps its scrollTop across
    // re-renders — so a second search could open already scrolled down a list it no longer
    // describes, hiding the first (and default-selected) card.
    //
    // Keyed on the joined ids rather than the arrays themselves: the parent rebuilds those on
    // every render, so an array-identity dependency would fire on every render instead.
    // Declared above the `total === 0` early return — hooks cannot run conditionally.
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const recordKey = [...safeTenants, ...safeLeads].map((t) => t.accountUuid).join("|");
    React.useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [recordKey]);

    const selected = [...safeTenants, ...safeLeads].find((t) => t.accountUuid === selectedAccountUuid)
        || safeTenants[0] || safeLeads[0] || null;
    // Which unit the call was about is a separate question from which account, and it only
    // arises when the selected tenancy holds more than one lease.
    const unitOptions = selected && selected.targetType === "tenant" && selected.units.length > 1
        ? selected.units
        : [];

    if (total === 0) return null;
    // A single match still gets a card. It is the record the note will attach to, so it
    // deserves the same visual anchor as the multi-match case — without one the modal
    // degenerated into two stacked full-width bars that each repeated the caller's name.
    // The heading and helper text are dropped though: there is nothing to choose between.
    const isSingle = total === 1;

    const renderCard = (tenant: MonumentTenant) => {
        // The lone card is always the target, whether or not the parent has set the id yet.
        const isSelected = isSingle || selectedAccountUuid === tenant.accountUuid;
        const isLead = tenant.targetType === "lead";
        return (
            <div key={tenant.accountUuid} className={isSingle ? "col-12" : "col-md-6 col-lg-3"}>
                <div
                    className={`card h-100 ${isSelected ? 'border-2 border-primary bg-primary bg-opacity-10 shadow' : 'border border-secondary'}`}
                    style={{
                        cursor: isSingle ? 'default' : (disabled ? 'not-allowed' : 'pointer'),
                        transition: 'all 0.2s ease-in-out',
                        // The lone card has nothing to stand out FROM, and scaling it shaves its
                        // top border off: with no heading above it, it sits flush against the
                        // scroll container's clip box and the 2px overflow is cut (see below).
                        transform: isSelected && !isSingle ? 'scale(1.02)' : 'scale(1)',
                        opacity: disabled ? 0.6 : 1,
                    }}
                    onClick={() => { if (!disabled && !isSingle) onSelect(tenant.accountUuid); }}
                    role={isSingle ? undefined : "button"}
                    aria-pressed={isSingle ? undefined : isSelected}
                >
                    {isSelected && (
                        <div className="position-absolute top-0 end-0 p-1">
                            <i className="bi bi-check-circle-fill text-primary bg-white rounded-circle" style={{ fontSize: '1.1rem' }}></i>
                        </div>
                    )}
                    <div className={isSingle ? "card-body p-3 d-flex align-items-center gap-3" : "card-body p-2 text-center"}>
                        <div className={isSingle ? "" : "mb-1"}>
                            <i className={`bi ${isLead ? 'bi-person-plus' : 'bi-person-circle'} ${isSelected ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <div className={isSingle ? "text-start" : ""}>
                        <h6 className={`card-title mb-1 ${isSelected ? 'fw-bold' : ''}`} style={{ fontSize: isSingle ? '0.95rem' : '0.85rem' }}>
                            {`${tenant.firstName} ${tenant.lastName}`.trim() || (isLead ? "Unnamed prospect" : "Unnamed tenant")}
                        </h6>
                        {tenant.phone && (
                            <p className="card-text mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                <i className="bi bi-telephone me-1"></i>
                                {tenant.phone}
                            </p>
                        )}
                        {/* A prospect has never rented, so it has no unit — its enquiry date and
                            status are what distinguish one card from another. */}
                        {isLead ? (
                            <>
                                {tenant.lead?.enquiredDate && (
                                    <p className="card-text mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-calendar-event me-1"></i>
                                        Enquired {formatMonumentDate(tenant.lead.enquiredDate)}
                                    </p>
                                )}
                                {tenant.lead?.facilityName && (
                                    <p className="card-text mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-building me-1"></i>
                                        {tenant.lead.facilityName}
                                    </p>
                                )}
                                {/* Colours are set explicitly rather than with utility classes.
                                    Bootstrap's .badge hard-codes white text (--bs-badge-color:#fff),
                                    and the *-subtle / *-emphasis utilities that would have overridden
                                    it only exist in Bootstrap 5.3 — this project is on 5.2.3, so they
                                    did nothing and the status rendered white-on-white: invisible on an
                                    unselected card and barely legible on a selected one. */}
                                {tenant.lead?.status && (
                                    <span
                                        className="d-inline-block mt-1"
                                        style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            lineHeight: 1.2,
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            color: '#334155',
                                            backgroundColor: '#e8ebf0',
                                            border: '1px solid #cbd3de',
                                        }}
                                    >
                                        {tenant.lead.status}
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                {tenant.unitSummary && (
                                    <p className="card-text mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-geo-alt me-1"></i>
                                        {tenant.unitSummary.includes(",") ? `Units ${tenant.unitSummary}` : `Unit ${tenant.unitSummary}`}
                                    </p>
                                )}
                                {/* The facility is often the ONLY thing separating two cards — the
                                    search spans the whole portfolio, so same-named tenants at
                                    different facilities are otherwise identical. */}
                                {tenant.facilitySummary && (
                                    <p className="card-text mb-0 text-muted" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-building me-1"></i>
                                        {tenant.facilitySummary}
                                    </p>
                                )}
                            </>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="row mb-3">
            <div className="col-12">
                {total > 1 && (
                    <>
                        <label className="form-label fw-bold bg-light px-3 py-2 rounded mb-2 d-inline-flex align-items-center">
                            <i className="bi bi-people text-primary me-2"></i>
                            Select who this note is for
                        </label>
                        <div className="small text-muted mb-3">
                            This caller matches more than one Monument record. Pick the one the call
                            summary should be saved to.
                        </div>
                    </>
                )}

                {/* Both sections share ONE scroll area rather than scrolling separately.
                    Monument returns up to 25 records for a search, and the modal sets no height
                    of its own, so without this a busy caller stretches the pop-up past the
                    viewport and pushes Submit/Cancel off the bottom of the screen.

                    Capped in vh so the limit tracks the screen rather than a guessed pixel
                    count — the rest of the modal (search box, banner, unit chips, footer) has
                    to fit alongside it on a laptop. overflow-y is `auto`, so a caller with one
                    or two matches sees no scrollbar and nothing about the common case changes.

                    Two sections in one region rather than two: nested scrollbars are hard to
                    use, and the tenants/leads split is a label, not a boundary the agent should
                    have to scroll within. The scrollbar is deliberately left VISIBLE — the
                    in-call panel hides its own, and an invisible scrollbar is the reason a
                    record below the fold reads as a record that does not exist. */}
                <div
                    className="monument-record-scroll"
                    ref={scrollRef}
                    style={{
                        // Floored so a short window still shows a usable slice: 42vh alone is
                        // ~170px on a 400px-tall window, less than one row of cards plus its
                        // section label. The floor only takes effect below ~525px of viewport.
                        maxHeight: 'max(220px, 42vh)',
                        overflowY: 'auto',
                        // Capping the vertical axis makes the browser compute overflow-x as
                        // `auto` too — CSS will not leave one axis visible when the other is
                        // clipped — and Bootstrap's .row carries -0.5rem side margins, which
                        // then read as horizontal overflow and raise a scrollbar nobody asked
                        // for. Pinned to hidden: there is nothing to scroll to sideways.
                        overflowX: 'hidden',
                        // Clipping happens at the PADDING box, and a selected card is drawn
                        // outside its column: transform scale(1.02) pushes it ~2px past each
                        // edge, so with no side padding the browser shaved the left border off
                        // the leftmost selected card. This pads the clip box out and pulls the
                        // same amount back with a negative margin, so the cards stay aligned
                        // with the section labels and the text above exactly as before.
                        marginLeft: '-0.75rem',
                        marginRight: '-0.75rem',
                        paddingLeft: '0.75rem',
                        paddingRight: '0.75rem',
                    }}
                >
                    {safeTenants.length > 0 && (
                        <>
                            {/* The section LABEL is only meaningful when there is another section to
                                tell it apart from; the cards themselves always render. */}
                            {total > 1 && (
                                <div className="fw-semibold text-uppercase text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                    Tenants
                                </div>
                            )}
                            <div className="row g-3 mb-3">{safeTenants.map(renderCard)}</div>
                        </>
                    )}

                    {safeLeads.length > 0 && (
                        <>
                            {total > 1 && (
                                <div className="fw-semibold text-uppercase text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                    Leads
                                </div>
                            )}
                            <div className="row g-3 mb-3">{safeLeads.map(renderCard)}</div>
                        </>
                    )}
                </div>

                {/* Monument notes attach to the account, not to a lease, so for a tenant holding
                    several units the note text is the only place the unit can be recorded.
                    Choosing nothing is valid — the note then names every unit rather than
                    guessing one. */}
                {unitOptions.length > 0 && (
                    <div className="mt-1">
                        <div className="fw-semibold text-uppercase text-muted mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                            Which unit was this call about?
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {unitOptions.map((u) => {
                                const active = selectedUnitLabel === u.label;
                                return (
                                    <button
                                        key={u.label}
                                        type="button"
                                        disabled={disabled}
                                        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={() => { if (!disabled && onSelectUnit) onSelectUnit(active ? "" : u.label); }}
                                    >
                                        {u.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="small text-muted mt-1">
                            Optional — leave unselected and the note will list all units on this account.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonumentTenantPicker;
